import { api } from "../api/client";
import { downloadJson } from "../lib/download";
import { formatDateTime } from "../lib/format";
import { ui } from "../lib/ui";
import { useAsync } from "../lib/useAsync";
import { useTransientStatus } from "../lib/useTransientStatus";
import type { CameraConfig, CameraMarker, FleetRow } from "../types";
import { AsyncBlock } from "./AsyncBlock";
import { Panel } from "./Panel";
import { PlanogramView } from "./PlanogramView";

/** One branch's floor plan and camera setup as the hub last received it
 *  (`GET /branches/{id}/setup` — the same document the branch PC syncs after
 *  every plan or camera change). Read-only: the branch PC owns its setup, the
 *  hub only looks. A store whose PC has not synced yet has nothing to show.
 *
 *  The same document downloads as a file — the one the replacement PC for a
 *  dead branch machine imports (Store › Floor plan & cameras › Import setup…),
 *  so a store is drawn once even when its PC is not the one that drew it. */

/** A plan marker with its entrance config when it has one, plus configs whose
 *  marker is missing from the plan — the setup and the plan are edited in the
 *  same panel on the PC, but a camera can be set up and then deleted off the
 *  plan, and that mismatch is exactly what a fleet operator wants to see. */
interface CameraRow {
  id: string;
  label: string;
  marker: CameraMarker | null;
  config: CameraConfig | null;
}

function cameraRows(
  markers: CameraMarker[],
  configs: Record<string, CameraConfig>,
): CameraRow[] {
  const rows: CameraRow[] = markers.map((marker) => ({
    id: marker.id,
    label: marker.label || marker.id,
    marker,
    config: configs[marker.id] ?? null,
  }));
  for (const [id, config] of Object.entries(configs)) {
    if (!markers.some((m) => m.id === id)) rows.push({ id, label: id, marker: null, config });
  }
  return rows;
}

export function BranchPlanPanel({ row, onClose }: { row: FleetRow; onClose: () => void }) {
  const setup = useAsync(() => api.getBranchSetup(row.branch_id), [row.branch_id]);
  const [status, setStatus] = useTransientStatus();
  const plan = setup.data?.planogram ?? null;
  const cameras = cameraRows(plan?.cameras ?? [], setup.data?.cameras ?? {});
  const empty = !plan && cameras.length === 0;

  // The document already in hand, saved as it stands — no second request, and
  // nothing is sent anywhere.
  const download = () => {
    if (!setup.data) return;
    downloadJson(`${row.branch_id}-setup.json`, setup.data);
    setStatus(
      `Downloaded ${Object.keys(setup.data.cameras).length} camera setup(s)` +
        `${setup.data.planogram ? " and the floor plan" : " (no floor plan)"}`,
    );
  };

  return (
    <Panel
      title={`${row.name} · floor plan`}
      subtitle={
        `${row.governorate}${row.area ? ` › ${row.area}` : ""} · ${row.branch_id} · ` +
        (row.last_setup_at
          ? `setup last received ${formatDateTime(row.last_setup_at)}`
          : "no setup received yet")
      }
      actions={
        <div className="flex gap-2">
          <button
            type="button"
            className={ui.btnQuiet}
            disabled={empty || setup.loading}
            title="Save this branch's plan and camera setup as a file — what a replacement PC imports"
            onClick={download}
          >
            Download setup
          </button>
          <button type="button" className={ui.btnQuiet} onClick={setup.reload}>
            Refresh
          </button>
          <button type="button" className={ui.btnQuiet} onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {status && <p className={`${ui.notice} ${ui.noticeSuccess}`}>{status}</p>}
      <AsyncBlock
        loading={setup.loading}
        error={setup.error}
        empty={empty}
        emptyMessage={
          "This branch's PC has not sent a floor plan yet. It arrives on its own once the " +
          "store is drawn on that PC (Store › Floor plan & cameras) and the PC is connected."
        }
      >
        {plan ? (
          <>
            <p className="mb-3 text-[13px] text-muted">
              {plan.width.toFixed(1)} × {plan.height.toFixed(1)} m ·{" "}
              {plan.fixtures.length} fixture(s) · {(plan.walls ?? []).length} wall(s) ·{" "}
              {plan.cameras.length} camera(s), {plan.cameras.filter((c) => c.entry).length}{" "}
              at an entrance
            </p>
            <PlanogramView plan={plan} />
          </>
        ) : (
          <p className={`${ui.notice} text-muted`}>
            This branch has entrance cameras but no floor plan — its heatmap has nothing to
            draw on until the store is drawn on its PC.
          </p>
        )}

        {cameras.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Camera</th>
                  <th>Role</th>
                  <th>Sampling</th>
                  <th>Confidence</th>
                  <th>Entry line</th>
                  <th>Frame</th>
                </tr>
              </thead>
              <tbody>
                {cameras.map((camera) => {
                  const line = camera.config?.entry_line;
                  return (
                    <tr key={camera.id}>
                      <td>
                        <div className="font-medium text-text">{camera.label}</div>
                        <div className="font-mono text-xs text-muted">{camera.id}</div>
                      </td>
                      <td>
                        {camera.config ? (
                          <span className={ui.pill}>entrance</span>
                        ) : (
                          <span className="text-muted">occupancy only</span>
                        )}
                        {!camera.marker && (
                          <div className="text-xs text-danger">not on the plan</div>
                        )}
                      </td>
                      <td>{camera.config ? `${camera.config.fps_sample} fps` : "—"}</td>
                      <td>{camera.config ? camera.config.detection_confidence.toFixed(2) : "—"}</td>
                      <td className="font-mono text-xs">
                        {line
                          ? `${line.start[0]},${line.start[1]} → ${line.end[0]},${line.end[1]} · in: ${line.inward}`
                          : "—"}
                      </td>
                      <td className="font-mono text-xs">
                        {camera.config?.frame_size
                          ? `${camera.config.frame_size[0]}×${camera.config.frame_size[1]}`
                          : "native"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AsyncBlock>
    </Panel>
  );
}
